import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Stethoscope, Menu, X, ArrowUpRight } from "lucide-react";
import { Magnetic } from "./primitives";

const LINKS = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Studio", href: "#studio" },
  { label: "Process", href: "#process" },
];

export const Logo = ({ dark = false }) => (
  <a href="#top" data-testid="nav-logo" className="group flex items-center gap-2 select-none">
    <span
      className="grid place-items-center h-9 w-9 rounded-full transition-colors duration-300"
      style={{ background: "var(--dm-maroon)" }}
    >
      <Stethoscope size={18} className="text-white" strokeWidth={2.2} />
    </span>
    <span className="font-display text-xl font-extrabold tracking-tight leading-none">
      <span style={{ color: dark ? "#fff" : "var(--dm-teal)" }}>doctor</span>
      <span style={{ color: "var(--dm-maroonbright)" }}>.media</span>
    </span>
  </a>
);

export default function Navbar({ onCta }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = !scrolled;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500"
      style={{
        background: scrolled ? "rgba(251,251,251,0.72)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(39,90,113,0.12)" : "1px solid transparent",
      }}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo dark={dark} />

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-oswald text-sm uppercase tracking-[0.14em] relative group"
              style={{ color: dark ? "rgba(255,255,255,0.85)" : "var(--dm-dark)" }}
            >
              {l.label}
              <span
                className="absolute -bottom-1 left-0 h-[2px] w-0 group-hover:w-full transition-[width] duration-300"
                style={{ background: "var(--dm-maroonbright)" }}
              />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Magnetic strength={0.5} className="hidden sm:block">
            <button
              onClick={onCta}
              data-testid="nav-cta"
              className="group inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-oswald text-sm uppercase tracking-[0.12em] text-white transition-colors duration-300"
              style={{ background: "var(--dm-teal)" }}
            >
              Start a project
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Magnetic>
          <button
            className="md:hidden grid place-items-center h-10 w-10 rounded-full"
            style={{ color: dark ? "#fff" : "var(--dm-dark)", border: `1px solid ${dark ? "rgba(255,255,255,0.3)" : "rgba(10,10,10,0.15)"}` }}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-dm-dark"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl font-bold text-white"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setOpen(false); onCta(); }}
                className="mt-2 rounded-full px-5 py-3 font-oswald uppercase tracking-wide text-white text-left"
                style={{ background: "var(--dm-maroon)" }}
                data-testid="mobile-cta"
              >
                Start a project
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
